import type { CartPersona, Category, OrderItem, Reservation, Service, TableStatus } from "@lemmonhq/types";
import { nanoid } from "nanoid";

export type UpdateActionNone = { type: "none" };
export type UpdateActionNotify = { type: "toast"; message: string };
export type UpdateActionReload = {
  type: "reload";
  title: string;
  message: string;
  actionLabel: string;
};
export type UpdateActionRestart = {
  type: "restart";
  title: string;
  message: string;
  actionLabel: string;
};
export type UpdateActionUpdate = {
  type: "update";
  title: string;
  message: string;
  url: string;
};

export type UpdateAction =
  | UpdateActionNone
  | UpdateActionNotify
  | UpdateActionReload
  | UpdateActionRestart
  | UpdateActionUpdate;

export type TableAvailability = "free" | "upcoming" | "busy";

export interface TableWithAvailability extends TableStatus {
  availability: TableAvailability;
  nextReservationTime?: string;
  nextReservationDuration?: number;
}

type TableClassificationOptions = {
  bufferMinutes?: number;
  upcomingWindowHours?: number;
};

type CreateSeatPersonasInput = {
  count: number;
  existingCount?: number;
  createId: () => string;
};

type CreateSeatPersonasResult = {
  customers: CartPersona[];
  firstCustomerId?: string;
};

declare global {
  var nativeFetchBridge: Record<string, { resolve: Function; reject: Function }>;
  var ReactNativeWebView: {
    postMessage: Function;
  };
}

export const pp = <T>(value: T): T => JSON.parse(JSON.stringify(value));

export const sanitizeOrderNote = (note: string): string => {
  if (!note) return "";
  return note.replace(/[\r\n]+/g, " ");
};

export const sortCatalogItems = <T extends Service | Category>(items: T[]): T[] => {
  return items.sort((a, b) => a.order - b.order);
};

export const filterServicesBySchedule = (services: Service[]): Service[] => {
  const now = new Date();
  const currentDay = now.toLocaleDateString("en-US", { weekday: "short" });
  const currentTime = now.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });

  return sortCatalogItems(services).filter((service) => {
    // No schedule at all (null, or `{}` / only empty days as the catalog
    // editor saves it) means "Always open", matching what the editor shows.
    const days = service.days;
    const hasSchedule =
      !!days &&
      Object.values(days).some(
        (ranges) => ranges && Object.values(ranges).length > 0,
      );
    if (!days || !hasSchedule) {
      return true;
    }

    let daySchedule = days[currentDay];

    if (!daySchedule || daySchedule.length === 0) {
      return false;
    }

    if (typeof daySchedule === "object" && !Array.isArray(daySchedule)) {
      daySchedule = Object.values(daySchedule);
    }

    return daySchedule.some((schedule) => {
      const start = schedule.start;
      const end = schedule.end;

      if (end < start) {
        return currentTime <= end || currentTime >= start;
      }

      return currentTime >= start && currentTime <= end;
    });
  });
};

export function normalizeFoodTypeName(name?: string) {
  return (name || "")
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[\/,_-]+/g, " ")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function getFoodTypeIconName(name?: string) {
  const normalized = normalizeFoodTypeName(name);

  if (!normalized) {
    return "restaurant";
  }

  const iconMatchers: Array<{ pattern: RegExp; icon: string }> = [
    { pattern: /\b(pizza|pinsa|pinse)\b/, icon: "local_pizza" },
    { pattern: /\b(cookie|cookies|biscuit|biscuits)\b/, icon: "cookie" },
    {
      pattern: /\b(cake|cakes|dessert|desserts|dolci|dolcezze|douceur|douceurs|sweet|sweets)\b/,
      icon: "cake",
    },
    { pattern: /\b(ice cream|gelato|shaved ice)\b/, icon: "icecream" },
    {
      pattern:
        /\b(starter|starters|appetizer|appetizers|appetiser|appetisers|entree|entrees|antipasti|antipasto|tapas|vorspeise|aperitif|aperitivi|a partager|to share|share)\b/,
      icon: "soup_kitchen",
    },
    { pattern: /\b(salad|salads|salatebi|salade|salades)\b/, icon: "nutrition" },
    { pattern: /\b(soup|soups)\b/, icon: "soup_kitchen" },
    { pattern: /\b(khinkali|ramen)\b/, icon: "ramen_dining" },
    { pattern: /\b(soba)\b/, icon: "soba" },
    { pattern: /\b(udon)\b/, icon: "udon" },
    { pattern: /\b(pasta|pates|pate)\b/, icon: "dinner_dining" },
    { pattern: /\b(taco|tacos|kebab|wrap|panini|panino)\b/, icon: "kebab_dining" },
    { pattern: /\b(bagel|toast|toasts|sandwich|sandwiches)\b/, icon: "brunch_dining" },
    {
      pattern: /\b(burger|fast ?food|snack|snacks|side|sides|garniture|garnitures|contorni|extras|extra)\b/,
      icon: "fastfood",
    },
    {
      pattern:
        /\b(menu|plat du jour|plat principal|hauptgericht|hauptgang|main course|main dishes|main|principal|plat|plats|piatti|assiette|assiettes|food)\b/,
      icon: "brunch_dining",
    },
    { pattern: /\b(morning|breakfast|brunch|egg|bagel)\b/, icon: "breakfast_dining" },
    { pattern: /\b(gluten free|sans gluten)\b/, icon: "wheat" },
    { pattern: /\b(vegan|vegane)\b/, icon: "nutrition" },
    {
      pattern: /\b(liquor|alcohol|alcool|alcol|wine|beer|apero|apero time|aperitif|aperitivi|bar)\b/,
      icon: "local_bar",
    },
    { pattern: /\b(coffee|cafe|tea)\b/, icon: "local_cafe" },
    { pattern: /\b(drink|drinks|boisson|boissons|beverage|beverages)\b/, icon: "local_drink" },
    { pattern: /\b(oven|four)\b/, icon: "oven" },
    { pattern: /\b(grill|bbq|barbecue|yakitori)\b/, icon: "outdoor_grill" },
    { pattern: /\b(children|kids|enfants)\b/, icon: "parent_child_dining" },
    { pattern: /\b(hotel)\b/, icon: "room_service" },
    { pattern: /\b(menu)\b/, icon: "set_meal" },
    { pattern: /\b(share|sharing|a partager|small plate|petit faim)\b/, icon: "dining" },
    { pattern: /\b(a emporter|emporter|take away|takeaway|to go)\b/, icon: "takeout_dining" },
    { pattern: /\b(appetizer|starter|entree|main|dessert|drink|food)\b/, icon: "dining" },
  ];

  const match = iconMatchers.find(({ pattern }) => pattern.test(normalized));
  return match?.icon || "restaurant";
}

export const createSeatPersonas = ({
  count,
  existingCount = 0,
  createId,
}: CreateSeatPersonasInput): CreateSeatPersonasResult => {
  const customers: CartPersona[] = [];

  for (let index = 0; index < count; index += 1) {
    customers.push({
      id: createId(),
      name: `Seat ${existingCount + index + 1}`,
    });
  }

  return {
    customers,
    firstCustomerId: customers[0]?.id,
  };
};

const objectToPathString = (obj: Record<string, string | number>) => {
  return Object.entries(obj)
    .map(([key, value]) => `${key}/${value}`)
    .join("/");
};

export const getTaxRcptPrintJobId = (payment: { id: number | string }) => {
  return objectToPathString({
    type: "reciept",
    payment: payment.id.toString(),
    time: Date.now() / 1000,
  }).toString();
};

export function getOrderItemGroupId(orderItem: OrderItem): string {
  const baseId = `product:${orderItem.product_id}`;

  if (orderItem.product.is_custom) {
    return baseId + ":custom:" + orderItem.price;
  }

  if (!orderItem.bundles || orderItem.bundles.length === 0) {
    return baseId;
  }

  const bundleIds = orderItem.bundles
    .map((bundle) => `bundle:${bundle.entity_type}:${bundle.entity_id}`)
    .sort()
    .join("|");

  const rawString = `${baseId}|${bundleIds}`;
  return btoa(unescape(encodeURIComponent(rawString))).replace(/[^a-zA-Z0-9]/g, "");
}

export function hasTimeConflict(start1: Date, end1: Date, start2: Date, end2: Date, bufferMs: number): boolean {
  const bufferedStart1 = new Date(start1.getTime() - bufferMs);
  const bufferedEnd1 = new Date(end1.getTime() + bufferMs);
  const bufferedStart2 = new Date(start2.getTime() - bufferMs);
  const bufferedEnd2 = new Date(end2.getTime() + bufferMs);

  return (
    (bufferedStart1 >= bufferedStart2 && bufferedStart1 <= bufferedEnd2) ||
    (bufferedEnd1 >= bufferedStart2 && bufferedEnd1 <= bufferedEnd2) ||
    (bufferedStart2 >= bufferedStart1 && bufferedStart2 <= bufferedEnd1)
  );
}

export function classifyTableAvailability(
  table: TableStatus,
  targetReservation: Reservation,
  allReservations: Reservation[],
  options: TableClassificationOptions = {},
): {
  availability: TableAvailability;
  nextReservationTime?: string;
  nextReservationDuration?: number;
} {
  const bufferMinutes = options.bufferMinutes ?? 15;
  const upcomingWindowHours = options.upcomingWindowHours ?? 2;
  const bufferMs = bufferMinutes * 60 * 1000;

  const reservationTime = new Date(targetReservation.time);
  const reservationEndTime = new Date(reservationTime.getTime() + targetReservation.duration_mins * 60000);

  if (table.orders && table.orders.length > 0) {
    return { availability: "busy" };
  }

  const reservationsForTable = allReservations.filter((reservation) =>
    reservation.table_assignments?.some((assignment) => assignment.table_id === table.id),
  );

  let availability: TableAvailability = "free";
  let nextReservationTime: string | undefined;
  let nextReservationDuration: number | undefined;

  for (const reservation of reservationsForTable) {
    if (reservation.id === targetReservation.id) continue;

    const existingStart = new Date(reservation.time);
    const existingEnd = new Date(existingStart.getTime() + reservation.duration_mins * 60000);

    if (hasTimeConflict(reservationTime, reservationEndTime, existingStart, existingEnd, bufferMs)) {
      return {
        availability: "busy",
        nextReservationTime: reservation.time,
        nextReservationDuration: reservation.duration_mins,
      };
    }

    const upcomingWindowMs = upcomingWindowHours * 60 * 60 * 1000;
    if (
      existingStart > reservationEndTime &&
      existingStart <= new Date(reservationEndTime.getTime() + upcomingWindowMs)
    ) {
      if (availability === "free") {
        availability = "upcoming";
        nextReservationTime = reservation.time;
        nextReservationDuration = reservation.duration_mins;
      }
    }
  }

  return { availability, nextReservationTime, nextReservationDuration };
}

export function classifyTables(
  tables: TableStatus[],
  targetReservation: Reservation,
  allReservations: Reservation[],
  options?: TableClassificationOptions,
): TableWithAvailability[] {
  return tables.map((table) => {
    const { availability, nextReservationTime, nextReservationDuration } = classifyTableAvailability(
      table,
      targetReservation,
      allReservations,
      options,
    );

    return {
      ...table,
      availability,
      nextReservationTime,
      nextReservationDuration,
    };
  });
}

globalThis.nativeFetchBridge = globalThis.nativeFetchBridge || {};

const NULL_BODY_STATUSES = [101, 204, 205, 304];

// timeoutSeconds is enforced by the native app (it aborts the request); it defaults to 10s there when omitted
export const fetchWithNativeBridge = async function (
  url: RequestInfo | URL,
  options: RequestInit & { timeoutSeconds?: number } = {},
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const requestId = nanoid() + nanoid();
    const payload = {
      requestId,
      url,
      timeout: options.timeoutSeconds,
      options: {
        method: options.method || "GET",
        headers: options.headers || {},
        body: options.body || null,
      },
    };

    // Both callbacks must always settle the promise and free the entry: they
    // run inside a script native injects, so a throw here would leave the
    // caller (printJob, pingPrinter) waiting forever.
    globalThis.nativeFetchBridge[requestId] = {
      resolve: (str: string) => {
        console.log("resolve called", str);
        try {
          const response = JSON.parse(str);
          // Response() throws when a null-body status is given a body ("").
          const nullBody = NULL_BODY_STATUSES.includes(response.status);
          resolve(
            new Response(nullBody ? null : response.body, {
              status: response.status,
              headers: response.headers,
            }),
          );
        } catch (e) {
          reject(e instanceof Error ? e : new Error("NATIVE_REQUEST_FAILED"));
        } finally {
          delete globalThis.nativeFetchBridge[requestId];
        }
      },
      reject: (str: string) => {
        console.log("reject called " + JSON.stringify(str));
        try {
          const err: any = str ? JSON.parse(str) : undefined;
          reject(new Error(err?.message || "NATIVE_REQUEST_FAILED"));
        } catch {
          reject(new Error("NATIVE_REQUEST_FAILED"));
        } finally {
          delete globalThis.nativeFetchBridge[requestId];
        }
      },
    };

    globalThis.ReactNativeWebView.postMessage(JSON.stringify(payload));
  });
};
